import { Entity, LiveData } from '@toeverything/infra';
import Fuse from 'fuse.js';

import type { DocsService } from '../../doc';
import type { QuickSearchSession } from '../providers/quick-search-provider';
import type { QuickSearchGroup } from '../types/group';
import type { QuickSearchItem } from '../types/item';
import { highlighter } from '../utils/highlighter';

const group: QuickSearchGroup = {
  id: 'library',
  label: { i18nKey: 'Library' },
  score: 7,
};

/** Custom-property kind stamped on every uploaded library item. */
const LIBRARY_KIND = 'library-item';

interface LibraryItemLike {
  docId: string;
  originalName: string;
  mimeType: string;
}

interface LibraryPayload {
  docId: string;
}

/**
 * Cmdk quick-search session for Library items — uploaded papers and
 * datasets. Previously Cmd-K surfaced docs, notes, links, tags and
 * collections but NOT the library, forcing researchers to navigate to
 * `/library` separately to reach an uploaded PDF/CSV. This indexes
 * every `library-item` doc by its original filename so a paper is one
 * keystroke away from anywhere in the app.
 *
 * Data flows from `DocsService.allDocProperties$` — the same source the
 * `/library` page reads — so a fresh upload appears here immediately.
 */
export class LibraryQuickSearchSession
  extends Entity
  implements QuickSearchSession<'library', LibraryPayload>
{
  constructor(private readonly docsService: DocsService) {
    super();
  }

  query$ = new LiveData('');

  private readonly libraryData$ = this.docsService.allDocProperties$.map(
    allProps => {
      const items: LibraryItemLike[] = [];
      if (!allProps) return items;
      for (const [docId, props] of Object.entries(allProps)) {
        const record = props as Record<string, unknown>;
        if (record['custom:kind'] !== LIBRARY_KIND) continue;
        const originalName =
          typeof record['custom:originalName'] === 'string'
            ? (record['custom:originalName'] as string)
            : '';
        const mimeType =
          typeof record['custom:mimeType'] === 'string'
            ? (record['custom:mimeType'] as string)
            : '';
        items.push({ docId, originalName, mimeType });
      }
      return items;
    }
  );

  items$: LiveData<QuickSearchItem<'library', LibraryPayload>[]> =
    LiveData.computed(get => {
      const query = get(this.query$).trim();
      if (!query) return [];
      const items = get(this.libraryData$);
      if (items.length === 0) return [];

      const fuse = new Fuse(items, {
        keys: [{ name: 'originalName', weight: 1 }],
        includeMatches: true,
        includeScore: true,
        ignoreLocation: true,
        threshold: 0.35,
      });

      const result = fuse.search(query, { limit: 12 });

      return result.map<QuickSearchItem<'library', LibraryPayload>>(
        ({ item, matches, score = 1 }) => {
          const nameMatches = matches
            ?.filter(m => m.key === 'originalName')
            .flatMap(m =>
              m.indices.map(([s, e]) => [s, e + 1] as [number, number])
            );
          const title = (highlighter(
            item.originalName,
            '<b>',
            '</b>',
            nameMatches ?? []
          ) ??
            item.originalName) || { i18nKey: 'Untitled' };
          return {
            id: 'library:' + item.docId,
            source: 'library',
            label: {
              title,
              subTitle: libraryKindLabel(item.mimeType),
            },
            group,
            score: 1 - score,
            payload: { docId: item.docId },
          };
        }
      );
    });

  query(query: string) {
    this.query$.next(query);
  }
}

/** Coarse human label for the file kind, shown as the result subtitle. */
function libraryKindLabel(mimeType: string): string {
  const m = mimeType.toLowerCase();
  if (m === 'application/pdf') return 'PDF';
  if (
    m === 'text/csv' ||
    m === 'text/tab-separated-values' ||
    m.includes('spreadsheet') ||
    m.includes('excel')
  ) {
    return 'Dataset';
  }
  if (m.includes('word')) return 'Document';
  if (m === 'application/json' || m.startsWith('text/')) return 'Text';
  return 'File';
}
